// GenericLink.tsx
import React from "react";
import { Link, LinkProps } from "react-router-dom";
import { getIconByName } from "./getIconByName";
import {
    ButtonVariant,
    ButtonShape,
    ButtonSize,
    variantClasses,
    shapeClasses,
    sizeClasses,
} from "./buttonStyles";

/** Props para un link con las mismas variantes y tamaños. */
export interface GenericLinkProps
    extends Omit<LinkProps, "to" | "className" | "children"> {
    to: string; // la URL o ruta
    label?: React.ReactNode;
    variant?: ButtonVariant;
    shape?: ButtonShape;
    size?: ButtonSize;

    isIconOnly?: boolean;
    loading?: boolean; // si quieres simular "deshabilitado"
    customHeight?: string;

    icon?: React.ReactNode;
    iconPosition?: "left" | "right";
    iconLeftName?: string;
    iconRightName?: string;

    slotLeft?: React.ReactNode;
    slotRight?: React.ReactNode;

    className?: string; // para añadir más clases
    onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

/** Componente que SIEMPRE es un <Link> */
export const GenericLink: React.FC<GenericLinkProps> = ({
    to,
    label,
    variant = "brand",
    shape = "filled",
    size = "md",
    isIconOnly = false,
    loading = false,
    customHeight,
    style,
    icon,
    iconPosition = "left",
    iconLeftName,
    iconRightName,
    slotLeft,
    slotRight,
    className = "",
    onClick,
    ...rest
}) => {
    const finalClasses = `
    inline-flex items-center justify-center
    rounded-sm
    transition-all duration-200 ease-out
    ${variantClasses[variant] ?? ""}
    ${shapeClasses[shape] ?? ""}
    ${sizeClasses[size] ?? ""}
    disabled:opacity-50 disabled:cursor-not-allowed
    ${className}
  `;

    const defaultHeight = size === "md" && !customHeight ? "40px" : undefined;
    const linkStyle: React.CSSProperties = {
        ...style,
        height: customHeight || defaultHeight,
    };

    const leftIconByName = iconLeftName ? getIconByName(iconLeftName) : null;
    const rightIconByName = iconRightName ? getIconByName(iconRightName) : null;

    const innerContent = (
        <>
            {slotLeft && <span className="inline-flex items-center">{slotLeft}</span>}

            {leftIconByName && (
                <span className="inline-flex items-center mr-1 text-current">
                    {leftIconByName}
                </span>
            )}

            {icon && iconPosition === "left" && (
                <span className="inline-flex items-center mr-1 text-current">
                    {icon}
                </span>
            )}

            {!isIconOnly && label && (
                <span className="mx-1 whitespace-nowrap">{label}</span>
            )}

            {icon && iconPosition === "right" && (
                <span className="inline-flex items-center ml-1 text-current">
                    {icon}
                </span>
            )}

            {rightIconByName && (
                <span className="inline-flex items-center ml-1 text-current">
                    {rightIconByName}
                </span>
            )}

            {slotRight && <span className="inline-flex items-center">{slotRight}</span>}
        </>
    );

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (loading) {
            e.preventDefault();
        }
        onClick?.(e);
    };

    return (
        <Link
            to={to}
            className={finalClasses}
            style={linkStyle}
            onClick={handleClick}
            {...rest}
        >
            {innerContent}
        </Link>
    );
};

export default GenericLink;
